import { useTranslation } from 'next-i18next';
import React, {
  useMemo,
  useCallback,
  useState,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from 'react';
import { Editor, Transforms, Range, createEditor, Descendant, BaseEditor } from 'slate';
import { withHistory } from 'slate-history';
import { Slate, Editable, ReactEditor, withReact, RenderElementProps } from 'slate-react';

import { AvatarTag } from './AvatarTag';
import { TagMenu } from './TagMenu';
import { TAGS, ALWAYS_ALLOW_KEYS } from './constants';
import styles from './index.module.scss';
import { withMaxLength } from './plugins/withMaxLength';
import { withMentions } from './plugins/withMention';
import { richTextAreaStore } from './store';
import type {
  CursorLocation,
  CustomElement,
  ITag,
  RichTextAreaMethods,
  RichTextAreaProps,
} from './types';
import { deserialize, serialize } from './utils';
import { useSyncedState } from '../../../hooks/useSyncedState';
import { useAppSelector } from '../../../state';
import { track } from '../../../track';
import { PAGE } from '../../EditHeader';

type CustomEditor = BaseEditor &
  ReactEditor & { maxLength?: number; currentValueRef: React.RefObject<string | undefined> };
// Props 类型定义
declare module 'slate' {
  interface CustomTypes {
    Editor: CustomEditor;
    Element: CustomElement;
  }
}

// 主组件，半受控
const RichTextArea = forwardRef((props: RichTextAreaProps, ref: React.Ref<RichTextAreaMethods>) => {
  const {
    wrapperCls,
    wrapperStyle,
    maxLength,
    showCount = false,
    placeholder,
    initialValue,
    onChange,
    onFocus,
    onBlur,
    avatarSelector,
    nicknameSelector,
    id,
  } = props;

  const { t } = useTranslation();

  const npcId = useAppSelector((state) => state.edit.npcId);

  // 状态管理
  const [tagMenuAnchor, setTagMenuAnchor] = useState<Range | null>(null);
  const [tagMenuSelectedIndex, setTagMenuSelectedIndex] = useState(0);
  const [tagMenuPosition, setTagMenuPosition] = useState<{
    top: string | number;
    left: string | number;
  } | null>(null);
  const [currentValue, setCurrentValue, currentValueRef] = useSyncedState(initialValue);
  const [, setCursorLocation, cursorLocationRef] = useSyncedState<CursorLocation | null>(null);

  // 编辑器初始化
  const editor = useMemo(
    () =>
      withMaxLength(
        withMentions(withReact(withHistory(createEditor()))),
        currentValueRef,
        maxLength ?? 0
      ),
    []
  );

  // 文本序列化和反序列化

  const initEditorValue = useMemo(() => deserialize(initialValue || ''), []);

  const reset = useCallback((newValue?: string) => {
    Transforms.delete(editor, {
      at: {
        anchor: Editor.start(editor, []),
        focus: Editor.end(editor, []),
      },
    });

    Transforms.insertNodes(editor, deserialize(newValue || ''), {
      at: [0],
    });
    setCurrentValue(newValue || '');
    setTagMenuAnchor(null);
    setTagMenuSelectedIndex(0);
    setTagMenuPosition(null);
  }, []);

  useEffect(() => {
    // 注册到 store
    if (id) {
      richTextAreaStore.register(id, { reset });
    }
    return () => {
      if (id) {
        richTextAreaStore.unregister(id);
      }
    };
  }, [id, reset]);

  useImperativeHandle(ref, () => {
    const methods = {
      reset,
    };
    return methods;
  }, [reset]);

  // 更新光标位置更新函数
  const updateCursorLocation = useCallback(() => {
    const { selection } = editor;
    if (!selection) {
      setCursorLocation(null);
      return;
    }

    if (Range.isCollapsed(selection)) {
      // 单个光标位置
      setCursorLocation({
        type: 'cursor',
        path: selection.anchor.path,
        offset: selection.anchor.offset,
      });
    } else {
      // 选区范围
      setCursorLocation({
        type: 'range',
        anchor: {
          path: selection.anchor.path,
          offset: selection.anchor.offset,
        },
        focus: {
          path: selection.focus.path,
          offset: selection.focus.offset,
        },
      });
    }
  }, [editor]);

  const handleChange = useCallback(
    (value: Descendant[]) => {
      // 处理光标位置变化
      updateCursorLocation();
      const textContent = serialize(value);
      setCurrentValue(textContent);
      onChange?.({ target: { value: textContent } } as React.ChangeEvent<HTMLTextAreaElement>);
    },
    [onChange, setCurrentValue, updateCursorLocation]
  );

  const handleTagSelect = useCallback(
    (tag: ITag, offset = 0) => {
      track.send('add_character_quota', PAGE, {
        npc_id: npcId,
      });
      if (cursorLocationRef.current) {
        // 根据光标类型选择插入位置
        if (cursorLocationRef.current.type === 'cursor') {
          // 单个光标位置
          Transforms.select(editor, {
            path: cursorLocationRef.current.path,
            offset: cursorLocationRef.current.offset + offset,
          });
        } else {
          // 选区范围，替换选区内容
          Transforms.select(editor, {
            anchor: {
              path: cursorLocationRef.current.anchor.path,
              offset: cursorLocationRef.current.anchor.offset,
            },
            focus: {
              path: cursorLocationRef.current.focus.path,
              offset: cursorLocationRef.current.focus.offset + offset,
            },
          });
        }

        editor.insertMention?.(tag);
      } else {
        // 如果没有光标位置，插入到末尾
        Transforms.select(editor, Editor.end(editor, []));
        editor.insertMention?.(tag);
      }
    },
    [cursorLocationRef, editor, npcId]
  );

  const handleMenuSelect = useCallback(
    (tag: ITag) => {
      Transforms.delete(editor, { distance: 1, reverse: true });
      handleTagSelect(tag, -1); //delete后，光标ref来不及更新
      setTagMenuAnchor(null);
    },
    [editor, handleTagSelect]
  );

  // 处理键盘事件
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (
        maxLength &&
        (currentValueRef.current?.length || 0) >= maxLength &&
        !ALWAYS_ALLOW_KEYS.includes(event.key)
      ) {
        event.preventDefault();
        return false;
      }

      if (event.key === '@') {
        const { selection } = editor;

        if (selection) {
          const [start] = Range.edges(selection);

          // 设置目标范围
          const menuAnchorRange = {
            anchor: { path: start.path, offset: start.offset + 1 },
            focus: { path: start.path, offset: start.offset + 1 },
          };

          // 获取下拉菜单位置
          try {
            const domSelection = window.getSelection();
            if (domSelection && domSelection.rangeCount > 0) {
              const range = domSelection.getRangeAt(0);
              const rect = range.getBoundingClientRect();

              setTagMenuPosition({
                top: `calc(${rect.top + 5}px + 1em)`, //避免遮住当前输入行
                left: rect.left,
              });

              setTagMenuAnchor(menuAnchorRange);
              setTagMenuSelectedIndex(0);
            }
          } catch (error) {
            console.error('Error setting tag menu position:', error);
          }
        }
        return;
      }

      // 处理选择列表的键盘操作
      switch (event.key) {
        case 'ArrowDown':
          if (tagMenuAnchor) {
            event.preventDefault();
            setTagMenuSelectedIndex((i) => (i >= TAGS.length - 1 ? 0 : i + 1));
          }
          break;
        case 'ArrowUp':
          if (tagMenuAnchor) {
            event.preventDefault();
            setTagMenuSelectedIndex((i) => (i <= 0 ? TAGS.length - 1 : i - 1));
          }
          break;
        case 'Enter':
          if (tagMenuAnchor) {
            event.preventDefault();
            handleMenuSelect(TAGS[tagMenuSelectedIndex]);
          }
          break;
        case 'Tab':
          if (tagMenuAnchor) {
            event.preventDefault();
            handleMenuSelect(TAGS[tagMenuSelectedIndex]);
          }
          break;
        default:
          if (event.key.length === 1 && tagMenuAnchor) {
            setTagMenuAnchor(null);
          }
          break;
      }
    },
    [currentValueRef, editor, handleMenuSelect, maxLength, tagMenuAnchor, tagMenuSelectedIndex]
  );

  // 渲染标签元素
  const renderElement = useCallback((props: RenderElementProps) => {
    const { attributes, children, element } = props;

    if (element.type === 'mention' && element.tag) {
      return (
        <AvatarTag
          {...element.tag}
          avatarSelector={avatarSelector}
          nicknameSelector={nicknameSelector}
        />
      );
    }

    return <p {...attributes}>{children}</p>;
  }, []);

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      onFocus?.(e as any);
    },
    [onFocus]
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      onBlur?.(e);
      if (document.querySelector(`.${styles.tagMenu}`)?.contains(e.relatedTarget)) {
        e.preventDefault();
      } else {
        setTagMenuAnchor(null);
      }
    },
    [onBlur]
  );

  return (
    <div className={wrapperCls} style={wrapperStyle} onClick={() => setTagMenuAnchor(null)}>
      <Slate editor={editor} initialValue={initEditorValue} onChange={handleChange}>
        <Editable
          className={styles.editableContent}
          renderElement={renderElement}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          onFocus={handleFocus}
          onBlur={handleBlur}
          style={{
            minHeight: props.minRows ? `calc(${props.minRows * 1.5}em )` : undefined,
            maxHeight: props.maxRows ? `calc(${props.maxRows * 1.5}em )` : undefined,
          }}
        />
      </Slate>

      <TagMenu
        tags={TAGS}
        selectedIndex={tagMenuSelectedIndex}
        menuAnchor={tagMenuAnchor}
        menuPosition={tagMenuPosition}
        onSelect={(tag) => {
          handleMenuSelect(tag);
        }}
      />
      <hr className={styles.separator} />
      <div className={styles.tagSelect}>
        <span>{t('edit_mention_title')}</span>
        {TAGS.map((tag) => (
          <AvatarTag
            avatarSelector={avatarSelector}
            nicknameSelector={nicknameSelector}
            key={tag.value}
            {...tag}
            onClick={() => handleTagSelect(tag)}
          />
        ))}
      </div>
      {showCount && maxLength && (
        <section className={styles.counter}>
          {currentValue?.length ?? 0}/{maxLength}
        </section>
      )}
    </div>
  );
});

export default RichTextArea;
